import type { HTMLAttributes, ReactNode } from 'react';

import { assetUrl } from '../assets/asset-manifest.js';

export interface PageLayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Defaults to `wide`, matching `GameHostPage` (the one page whose width
   * was never a style choice — a real board+rails layout) — every other
   * page's width should match it (2026-09-20) rather than each picking its
   * own independently, which is how the app ended up with visibly different
   * widths from one page to the next. `readable` is kept only for a
   * single-message screen (404, the signed-out landing card) that a wide
   * container would visually strand.
   */
  width?: 'readable' | 'standard' | 'comfortable' | 'wide';
  /**
   * How the parchment plate meets the page's content. `plate` insets the
   * content behind its own gutters; `flush` drops them, for a page that
   * already draws its own frame right at the plate's edge — the plate is
   * sized to the page's content width, so any gutter here shows up as a band
   * of bare parchment outside that frame.
   */
  surface?: 'plate' | 'flush';
}

const WIDTH_CLASSES: Record<NonNullable<PageLayoutProps['width']>, string> = {
  readable: 'max-w-readable',
  standard: 'max-w-content',
  comfortable: 'max-w-comfortable',
  wide: 'max-w-shell',
};

const SURFACE_CLASSES: Record<NonNullable<PageLayoutProps['surface']>, string> = {
  plate: 'app-frame px-4 py-6 tablet:px-7 tablet:py-8 wide:px-10',
  flush: 'app-frame',
};

export function PageLayout({
  children,
  width = 'wide',
  surface = 'plate',
  className = '',
  ...rest
}: PageLayoutProps) {
  return (
    <div
      className={`mx-auto flex w-full flex-col gap-6 ${WIDTH_CLASSES[width]} ${SURFACE_CLASSES[surface]} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}

export function PageHeader({ title, description, eyebrow, actions }: PageHeaderProps) {
  return (
    <header className="flex flex-col justify-between gap-4 border-b border-divider pb-4 tablet:flex-row tablet:items-end">
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-accent">
            {eyebrow}
          </p>
        )}
        <h1 className="page-title">{title}</h1>
        {description && (
          <p className="mt-2 max-w-readable font-editorial text-textSecondary">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export interface SectionHeadingProps {
  children: ReactNode;
  action?: ReactNode;
  /** Overrides the default brass divider glyph with a section-specific icon (e.g. the Games page's mode glyphs). */
  icon?: ReactNode;
}

export function SectionHeading({ children, action, icon }: SectionHeadingProps) {
  return (
    <div className="ornamental-rule min-w-0">
      {icon ?? (
        <img
          src={assetUrl('decorDivider')}
          alt=""
          aria-hidden="true"
          width={64}
          height={64}
          className="hidden h-5 w-5 shrink-0 tablet:block"
        />
      )}
      <h2 className="section-title shrink-0">{children}</h2>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function ContentSidebarLayout({
  children,
  sidebar,
}: {
  children: ReactNode;
  sidebar: ReactNode;
}) {
  // The sidebar column is `auto`, not a hand-typed `18rem` that happens to
  // match `layout.sidebar`: the `<aside>` below carries `desktop:w-sidebar`
  // (the already-Tailwind-wired token) and the grid track sizes to it, so
  // the token is the only place this width is written rather than two
  // places that both have to agree.
  return (
    <div className="grid min-w-0 gap-6 desktop:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">{children}</div>
      <aside className="min-w-0 desktop:w-sidebar" aria-label="Contextual information">
        {sidebar}
      </aside>
    </div>
  );
}

export interface GameLayoutProps {
  children: ReactNode;
  leftRail?: ReactNode;
  rightRail?: ReactNode;
}

/**
 * Desktop (>=1024px, the `desktop` breakpoint token — docs/UI_GUIDELINES.md
 * §7: "Desktop (1024px+): content/sidebar compositions are available")
 * switches to a three-column grid with the rail(s) beside the centered
 * game surface. Below that, everything stacks in DOM order (content first,
 * then any rails beneath it) — the tablet behavior §7 also requires
 * ("collapse sidebars/rails beneath primary content"). Mobile gets no
 * special treatment here beyond the same stacking; a game with rail
 * content dense enough to need repositioning into a disclosure at the
 * narrowest widths does that itself inside its own rail content
 * (docs/games/word-rush/GAME_PLAN.md §11's mobile leaderboard disclosure
 * is one example), since only the game knows what that content is.
 */
export function GameLayout({ children, leftRail, rightRail }: GameLayoutProps) {
  // Both rail columns are `auto`, not a hand-typed `15rem` that happens to
  // match `layout.contextRail`: every element that can sit in either column
  // — the real `<aside>` and its empty placeholder alike — carries
  // `desktop:w-context-rail` (the already-Tailwind-wired token), so the
  // token is the only place this width is written. The empty placeholders
  // need the class too, not just `hidden desktop:block`: an `auto` track
  // sized against zero-width empty content would collapse to 0 instead of
  // reserving the rail's width when a game supplies no rail.
  return (
    <div className="grid min-w-0 gap-5 desktop:grid-cols-[auto_minmax(0,1fr)_auto]">
      {leftRail ? (
        <aside className="order-2 min-w-0 desktop:order-1 desktop:w-context-rail">{leftRail}</aside>
      ) : (
        <div className="hidden desktop:block desktop:w-context-rail" />
      )}
      <div className="order-1 min-w-0 desktop:order-2">{children}</div>
      {rightRail ? (
        <aside className="order-3 min-w-0 desktop:w-context-rail">{rightRail}</aside>
      ) : (
        <div className="hidden desktop:block desktop:w-context-rail" />
      )}
    </div>
  );
}
